import React, { useEffect, useState, useCallback } from "react";
import Wrap from "../components/Wrap";
import Card from "../components/ui/Card";
import { Link, useParams } from "react-router-dom";
import useAuthStore from "../services/AuthService";
import Header from "../components/layouts/Header";
import { LocalesList } from "../types/Locales";
import JsonEditorForm from "../components/JsonEditorForm";
import Spinner from "../components/ui/Spinner";
import {
  getLocaleGroups,
  getLocalesList,
  saveLocalesList,
  updateLocalesList,
} from "../services/LocalizeService";
import { useToast } from "../components/shadcn/toast/use-toast";
import { Toaster } from "../components/shadcn/toast/toaster";
import { ToastAction } from "../components/shadcn/toast/toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/shadcn/select";

const EditPage: React.FC = () => {
  const { user } = useAuthStore();
  const { owner, repo } = useParams();
  const { toast } = useToast();

  const [locales, setLocales] = useState<LocalesList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasCommits, setHasCommits] = useState(false);

  const [groups, setGroups] = useState<string[]>([]);
  const [activeGroup, setActiveGroup] = useState<string>("default");

  // Fetch available locale groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      if (!owner || !repo) return;

      const result = await getLocaleGroups(owner, repo);
      if (result && result.length > 0) {
        setGroups(result);
        setActiveGroup(result[0]);
      }
    };

    fetchGroups();
  }, [owner, repo]);

  // Fetch locales when active group changes
  useEffect(() => {
    const fetchLocales = async () => {
      if (!owner || !repo) return;

      setIsLoading(true);
      const response = await getLocalesList(owner, repo, activeGroup);
      setIsLoading(false);

      if (!response) {
        setIsError(true);
        return;
      }

      setIsError(false);
      setLocales(response.data);
      setHasCommits(response.hasCommits);
    };

    fetchLocales();
  }, [owner, repo, activeGroup]);

  const handleUpdateJson = useCallback(
    async (updatedJson: LocalesList[]) => {
      setLocales(updatedJson);

      if (!owner || !repo) return;

      setIsLoading(true);
      const response = await updateLocalesList(owner, repo);
      setIsLoading(false);

      if (response?.success) {
        toast({
          description: "Pull request successfully created.",
          action: (
            <ToastAction asChild altText="Open github pull request page">
              <Link to={response.data} target="_blank">
                Open
              </Link>
            </ToastAction>
          ),
        });
        setHasCommits(false);
      } else {
        toast({
          variant: "destructive",
          description: `An error occurred while creating the pull request. "${response?.data}"`,
        });
      }
    },
    [owner, repo, toast]
  );

  const handleSaveJson = useCallback(
    async (updatedJson: LocalesList[], commitMessage: string) => {
      setLocales(updatedJson);

      if (!owner || !repo) return;

      setIsLoading(true);
      const response = await saveLocalesList(
        owner,
        repo,
        updatedJson,
        commitMessage
      );
      setIsLoading(false);

      if (response) {
        toast({
          description:
            "The data was successfully saved to a separate branch in github",
          action: (
            <ToastAction asChild altText="Open github branch page">
              <Link to={response} target="_blank">
                Open
              </Link>
            </ToastAction>
          ),
        });
        setHasCommits(true);
      } else {
        toast({
          description: "An error occurred while save branch.",
        });
      }
    },
    [owner, repo, toast]
  );

  const showGroupSelector = groups.length > 1;

  return (
    <>
      <Header user={user} repositoryName={repo} owner={owner} />
      <Wrap>
        <Card>
          {showGroupSelector && (
            <div className="mb-4 flex items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                Locale group:
              </span>
              <Select value={activeGroup} onValueChange={setActiveGroup}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group === "default" ? "Default" : group.charAt(0).toUpperCase() + group.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            {locales && locales.length > 0 && !isLoading && (
              <div>
                <JsonEditorForm
                  localesList={locales}
                  onUpdate={handleUpdateJson}
                  onSave={handleSaveJson}
                  hasCommits={hasCommits}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col">
            {locales.length === 0 && !isLoading && (
              <div className="flex w-full justify-center">
                <span>There are no localization files yet</span>
              </div>
            )}
            {isLoading && (
              <div className="flex flex-col items-center gap-4 w-full justify-center">
                <Spinner className="w-8 h-8" />
                <span className="text-gray-400 uppercase">Loading...</span>
              </div>
            )}
          </div>

          <Toaster />
        </Card>
      </Wrap>
    </>
  );
};

export default EditPage;
